import React from 'react';
import { useTranslation } from 'react-i18next';
import Tippy from '@tippyjs/react';
import './ContractProgressBar.css';

/**
 * Contract spend progress bar with optional over-invoiced overflow.
 *
 * @param {Object} props
 * @param {number|null|undefined} props.contractValue
 * @param {number|null|undefined} props.spentPercentage - Decimal 0–1+ (can exceed 1 when over-invoiced)
 * @param {number|null|undefined} props.overInvoiced
 * @param {number|null|undefined} props.invoicedAmount - Invoiced revenue when there is no contract value
 * @param {function} props.formatCurrency
 * @param {function} props.formatPercent
 */
const ContractProgressBar = ({
  contractValue,
  spentPercentage,
  overInvoiced,
  invoicedAmount,
  formatCurrency,
  formatPercent,
}) => {
  const { t } = useTranslation();

  if (contractValue == null) {
    return (
      <div className="contract-progress">
        <div className="contract-progress-labels">
          <Tippy
            content={t('projects.details.invoicedExplanation')}
            placement="top"
            interactive={false}
            delay={[200, 0]}
            maxWidth={300}
          >
            <div className="contract-progress-label">
              <span className="contract-progress-label-text">
                {t('projects.details.contractBarSpent')}: {formatCurrency(invoicedAmount)}
              </span>
            </div>
          </Tippy>
        </div>
      </div>
    );
  }

  const spentPct = Math.max(0, Number(spentPercentage) || 0);
  const spentAmount = spentPct * Number(contractValue);
  const spentWithin = Math.min(spentPct, 1);
  const remainingWithin = Math.max(0, 1 - spentWithin);
  const hasOverflow = overInvoiced != null && Number(overInvoiced) > 0;

  // Visual widths: spent + remaining = 100% of contract; overflow is extra beyond the track.
  // When over-invoiced, spent fills the full track and overflow sits as a separate segment.
  const spentWidth = `${(spentWithin * 100).toFixed(2)}%`;
  const remainingWidth = `${(remainingWithin * 100).toFixed(2)}%`;
  // Overflow segment size relative to contract value for visual scale, capped for layout.
  const overflowRatio = hasOverflow
    ? Math.min(Number(overInvoiced) / Number(contractValue), 0.5)
    : 0;
  const overflowWidth = `${(overflowRatio * 100).toFixed(2)}%`;

  return (
    <div className="contract-progress">
      <div className="contract-progress-track-row">
        <div className="contract-progress-track" aria-hidden="true">
          {spentWithin > 0 && (
            <div
              className="contract-progress-segment contract-progress-spent"
              style={{ width: spentWidth }}
            />
          )}
          {remainingWithin > 0 && (
            <div
              className="contract-progress-segment contract-progress-remaining"
              style={{ width: remainingWidth }}
            />
          )}
        </div>
        {hasOverflow && (
          <div
            className="contract-progress-segment contract-progress-overflow"
            style={{ width: overflowWidth }}
            title={formatCurrency(overInvoiced)}
          />
        )}
      </div>

      <div className="contract-progress-labels">
        <Tippy
          content={t('projects.details.contractValueSpentPercentageExplanation')}
          placement="top"
          interactive={false}
          delay={[200, 0]}
          maxWidth={300}
        >
          <div className="contract-progress-label">
            <span className="contract-progress-swatch contract-progress-spent" />
            <span className="contract-progress-label-text">
              {t('projects.details.contractBarSpent')}: {formatCurrency(spentAmount)} ({formatPercent(spentPct)})
            </span>
          </div>
        </Tippy>
        {hasOverflow && (
          <Tippy
            content={t('projects.details.overInvoicedExplanation')}
            placement="top"
            interactive={false}
            delay={[200, 0]}
            maxWidth={300}
          >
            <div className="contract-progress-label">
              <span className="contract-progress-swatch contract-progress-overflow" />
              <span className="contract-progress-label-text">
                {t('projects.columns.overInvoiced')}: {formatCurrency(overInvoiced)}
              </span>
            </div>
          </Tippy>
        )}
      </div>
    </div>
  );
};

export default ContractProgressBar;
